/**
 * Permits fidelity Phase A — applicant-facing fee display at submit (docs/design/
 * permits-fidelity-ui.md §6, docs/features/permits-fidelity.md §A.5). Always shows all three fee
 * lines even though only the application fee is a real number at submit time — sets the applicant's
 * expectation for what's tracked and states plainly that nothing is collected online.
 */
import { formatDollars } from "./format.js";

interface PermitFeeBlockProps {
  appFee: number;
  /** Number of holding tanks read from the Sewer/Water schema's tank_count answer, or 0/undefined
   *  for a type with no tank field — the impact-fee line is omitted entirely in that case. */
  tankCount?: number;
  impactFeePerTank: number;
}

export function PermitFeeBlock({ appFee, tankCount, impactFeePerTank }: PermitFeeBlockProps): JSX.Element {
  const impactFee = tankCount && tankCount > 0 ? tankCount * impactFeePerTank : 0;
  // A type with its application fee waived (permit-type-fee-override = 0, e.g. Water) must read
  // "Free" — never "$0.00 due" or "pay the $0.00 application fee at City Hall".
  const appFeeWaived = appFee <= 0;
  return (
    <div className="td-feeblock">
      <h2 className="td-feeblock__title">Fees for this application</h2>
      <div>
        <div className="td-feeline td-feeline--due">
          <div>
            <div className="td-feeline__label">Application fee — due at submission</div>
          </div>
          <div className="td-feeline__amt">{appFeeWaived ? "Free" : formatDollars(appFee)}</div>
        </div>
        {tankCount && tankCount > 0 ? (
          <div className="td-feeline">
            <div>
              <div className="td-feeline__label">
                Impact fee
                <span className="td-feeline__sub">
                  {formatDollars(impactFeePerTank)} × {tankCount} holding tank{tankCount === 1 ? "" : "s"}
                </span>
              </div>
            </div>
            <div className="td-feeline__amt">{formatDollars(impactFee)}</div>
          </div>
        ) : null}
        <div className="td-feeline">
          <div>
            <div className="td-feeline__label">
              Total permit fee
              <span className="td-feeline__sub">
                {appFeeWaived
                  ? "Set by the Building Codes Officer at approval"
                  : `Set by the Building Codes Officer at approval — the ${formatDollars(appFee)} application fee is credited toward this total`}
              </span>
            </div>
          </div>
          <div className="td-feeline__amt">TBD</div>
        </div>
      </div>
      <p className="td-feeblock__note">
        {appFeeWaived ? (
          <>
            This permit has <strong>no application fee</strong>. Any total fee set at approval is
            tracked here but <strong>not paid online</strong> — pay it in person at City Hall.
          </>
        ) : (
          <>
            Fees are tracked here but <strong>not paid online</strong> — pay the {formatDollars(appFee)}{" "}
            application fee, and later the total, in person at City Hall.
          </>
        )}
      </p>
    </div>
  );
}
